import { Box, Chip, CircularProgress, Link, Stack } from '@mui/material';
import { Handle, Position, useStore } from '@xyflow/react';
import classnames from 'classnames';
import DataFlowDetailActionsContainer from 'js/components/data_flows/DataFlowDetailActionsContainer';
import FlowTypeIcon from 'js/components/pipelines/cards/FlowTypeIcon';
import { NODE_HEIGHT_SMALL } from 'js/components/pipelines/FlowNodes/consts';
import {
  FLOW_CANVAS_CONFIG,
  getDirectionConfig,
} from 'js/components/pipelines/FlowNodes/constants';
import { useExpandedNodes } from 'js/components/pipelines/FlowNodes/context/ExpandContext';
import { getRelativeNodesBounds } from 'js/components/pipelines/FlowNodes/utils';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { ReactComponent as MinusIcon } from 'new/components/icons/fi-sr-minus-small.svg';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-sr-plus-small.svg';
import { ReactComponent as FolderIcon } from 'new/components/icons/folder.svg';
import { ReactComponent as DetailsIcon } from 'new/components/icons/vertical-split-outlined.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-sr-refresh.svg';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { IconButton } from 'new/elements/IconButton/IconButton';
import PipelineStatusChip from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import Typography from 'new/elements/Typography';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import React, { memo, useEffect, useRef } from 'react';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { useMetricsAdapter } from 'new/state/metrics/adapter/useMetricsAdapter';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';
// import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader'; // Commented out - will be replaced with localized loading indicator
import { FlowCounters } from '../../cards/components/FlowCounters/FlowCounters';
import { RAGCounters } from '../../cards/components/RAGCounters/RAGCounters';
import classes from '../FlowNodes.module.scss';
import styles from './FlowNode.module.scss';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { REFRESH_PIPELINE_REQUEST } from 'js/constants/actionTypes';

interface FlowItemProps {
  data: {
    selected: boolean;
    height: number;
    expanded?: boolean;
    hideToolbar?: (flow) => boolean;
    onFitViewItem?: (flowNodeId: number, fit: boolean) => boolean;
    width: number;
    item: any;
    linksMap?: any;
    toggleCloneFlowModal?: () => void;
    project?: any;
    isSecondaryFlow?: boolean;
    embedded?: boolean;
    layoutDirection?: 'LR' | 'TB';
  };
}

export const SecondaryFlowNode = (props: FlowItemProps) => {
  const { data } = props;
  const layoutDirection = data?.layoutDirection ?? 'LR';
  const dirCfg = getDirectionConfig(layoutDirection);
  const {
    height = 500,
    width = dirCfg.secondaryFlow.defaultWidth,
    onFitViewItem = () => {},
    expanded,
    item,
    toggleCloneFlowModal,
    isSecondaryFlow,
    embedded,
    project,
  } = data;

  const meta = useResourceMeta(RESOURCE_TYPE_SOURCE, item.data_source_id, item.data_credentials);

  useIsLoading(REFRESH_PIPELINE_REQUEST);

  const flowId = item.flow_node_id || item.id;

  const { onExpandNode } = useExpandedNodes();
  const { requestFlowMetrics } = useMetricsAdapter();

  const { project: flowProject, renderProjectLabel } = item;

  const containerCfg = FLOW_CANVAS_CONFIG.shared.flowContainer;

  const { minWidth, minHeight } = useStore((store) => {
    const childNodes = Array.from(store.nodeLookup.values()).filter(
      (n) => n.parentId === 'flow-' + flowId,
    );
    const rect = getRelativeNodesBounds(childNodes);

    return {
      minWidth: rect.x + rect.width + containerCfg.paddingX,
      minHeight: rect.y + rect.height + NODE_HEIGHT_SMALL + containerCfg.paddingY,
      hasChildNodes: childNodes.length > 0,
    };
  }, isEqual);

  const pipelineRefreshingSelector = (flowNodeId) =>
    createSelector([(s: any) => s.pipelines.refreshing[flowNodeId]], (refreshing) => {
      return refreshing;
    });

  const idRefresh = useSelector(pipelineRefreshingSelector(flowId));

  const handleExpand = () => {
    if (idRefresh) return;
    if (!expanded && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      refreshPipelineById(flowId, { addToRoot: false }).then(() => {
        onExpandNode('flow-' + flowId);
      });
    } else {
      onExpandNode('flow-' + flowId);
    }
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    refreshPipelineById(flowId, { addToRoot: false });
    requestFlowMetrics(flowId, item.data_source_id);
  };

  /**
   * ISSUE: Missing Flow Items When Expanding Linked Flows
   *
   * Problem: Linked flows (secondary flows) often don't have complete items when
   * initially loaded. This happens because:
   * - Linked flows are fetched as part of the main flow, but may only include partial data
   * - The main flow fetch might not include all data_sets and data_sinks for linked flows
   * - Expanding a linked flow without complete items causes edges to fail
   *
   * Solution: Always fetch the linked flow definition when expanded, even if partial
   * items exist. This ensures we have complete data (data_sets and data_sinks) needed
   * to create edges and display all nodes.
   *
   * Note: We use addToRoot: false to indicate this is a secondary fetch. The reducer's
   * mergeWithLinkedFlows will preserve linked_flows correctly and only update entities
   * for this specific linked flow.
   */
  const { refreshPipelineById } = usePipelinesAdapter();
  const hasFetchedRef = useRef(false);

  const hasLinks = item.linked_flows?.length || item.flow_triggers?.length || isSecondaryFlow;

  // Secondary/linked flow handles always stay LR-style regardless of layout direction
  return (
    <div
      style={{
        position: 'relative',
        minHeight: 120,
      }}
      onClick={() => onFitViewItem(item, false)}
    >
      {hasLinks ? (
        <>
          <Handle
            isConnectable={false}
            id="target-flow"
            type="target"
            position={Position.Left}
            style={{
              top: '55px',
              transform: 'translateY(-50%)',
            }}
          />
          <Handle
            isConnectable={false}
            id="source-flow"
            type="source"
            position={Position.Left}
            style={{
              top: '55px',
              transform: 'translateY(-50%)',
            }}
          />
        </>
      ) : null}
      {item.flow_type !== 'rag' ? (
        <div onClick={handleExpand} className={classes.expandButton}>
          {idRefresh ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <CircularProgress size={16} color="primary" />
            </Box>
          ) : expanded ? (
            <MinusIcon />
          ) : (
            <PlusIcon />
          )}
        </div>
      ) : null}
      <div
        className={classes.flowNodeToolbar}
        style={{
          zIndex: 2,
        }}
      >
        <div className={classes.flowItemInfoContainer}>
          <Stack gap="8px" flex={1}>
            <Typography className={classes.flowItemName}>{item.name}</Typography>
            <Stack direction="row" gap="16px">
              <ConnectionTypeChip
                icon={meta?.iconSm}
                label={meta?.label}
                style={{ border: 'none', padding: 0 }}
              />
              <ChipWithCopy tooltipTitle="Copy Flow ID" toCopy={flowId}>
                ID {flowId}
              </ChipWithCopy>
              {flowProject && renderProjectLabel ? (
                <Chip
                  data-not-expanding="true"
                  onClickCapture={(e) => e.stopPropagation()}
                  color="warning"
                  classes={{
                    label: styles.chipLabel,
                    root: styles.chipRootProject,
                  }}
                  label={
                    <>
                      <FolderIcon width="12px" height="11px" />
                      <Link href={`/integrate/projects/${flowProject.id}`} variant="body3">
                        {flowProject.name}
                      </Link>
                    </>
                  }
                />
              ) : null}
              <FlowTypeIcon data={item} />
              <PipelineStatusChip
                id={flowId}
                status={item.status}
                archivalStatus={item.archival_status}
                showRuntimeIndicator={false}
              />
            </Stack>
            {item.flow_type === 'rag' ? (
              <RAGCounters data={item} isClickablePartHovered={false} hideArrow />
            ) : (
              <FlowCounters hideArrow isClickablePartHovered={false} source={item} />
            )}
          </Stack>
          <Stack direction="row" gap="4px" alignItems="center">
            <IconButton
              size="small"
              onClick={handleRefresh}
              title="Refresh flow and metrics"
              disabled={idRefresh}
            >
              <RefreshIcon style={{ width: '14px', height: '14px' }} />
            </IconButton>
            <DataFlowDetailActionsContainer
              data={item}
              projectId={project?.id || item.project_id}
              inPipeline
              toggleCloneFlowModal={toggleCloneFlowModal}
              isSecondaryFlow
              embedded={embedded}
            />
          </Stack>
        </div>
      </div>
      <div
        className={classnames(classes.nodesContainer, classes.nodesContainer_secondary, {
          [classes.nodesContainer_hidden]: !expanded,
        })}
        style={{
          height: !expanded ? 0 : height - dirCfg.flowVerticalSpacing,
          width: !expanded ? dirCfg.secondaryFlow.collapsedWidth : width,
          minHeight: !expanded ? 0 : minHeight - dirCfg.flowVerticalSpacing,
          minWidth: !expanded ? dirCfg.secondaryFlow.collapsedWidth : minWidth,
          position: 'relative',
        }}
      >
        {/* TODO: Replace with localized loading indicator scoped to flow elements area
        {expanded && idRefresh && (
          <ConnectionLoader loading={true} message="Loading Flow" />
        )}
        */}
      </div>
    </div>
  );
};

type IsEqualCompareObj = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

function isEqual(prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes
  );
}

export default memo(SecondaryFlowNode);
